/**
 * TmdbLookupModal — owner-only "🎬 TMDb SEARCH" poster + synopsis picker.
 *
 * Usage:
 *   <TmdbLookupModal
 *     open={showTmdb}
 *     initialQuery={channel.name || video.title}
 *     initialYear={video.year}
 *     mode="channel" | "video"
 *     targetId={channel.id | video.id}
 *     apiBase={API}
 *     authHeaders={authHeaders}
 *     onClose={() => setShowTmdb(false)}
 *     onApplied={(updatedDoc) => { … }}
 *   />
 *
 * Fetches candidates via `/api/tmdb/search`, renders a grid of poster
 * tiles, lets the owner tick "apply poster" and/or "apply synopsis",
 * then POSTs to `/api/tmdb/apply/{mode}/{targetId}`. Callback receives
 * the updated Channel or Video document so the parent can refresh
 * without a full refetch.
 *
 * Kept dependency-free: axios via prop is optional, we just use fetch
 * so this file drops cleanly into either surface (guide + terminal).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Search as SearchIcon, Film, Tv as TvIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function TmdbLookupModal({
  open,
  initialQuery = "",
  initialYear = null,
  mode,
  targetId,
  apiBase,
  authHeaders,
  onClose,
  onApplied,
  // Optional override: when set, the "APPLY" button POSTs a different
  // endpoint + payload instead of the usual `/tmdb/apply/{mode}/{id}`.
  // Used by the UNMATCHED panel's "apply show to every episode on
  // this channel" bulk flow so we don't have to fork the modal.
  applyOverride,
  applyLabel,
}) {
  const [q, setQ] = useState(initialQuery);
  const [year, setYear] = useState(initialYear || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // {level, text}
  const [picked, setPicked] = useState(null);
  const [applyPoster, setApplyPoster] = useState(true);
  const [applyDescription, setApplyDescription] = useState(true);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Reset per-open so a second open on a different target starts fresh.
  useEffect(() => {
    if (!open) return;
    setQ(initialQuery || "");
    setYear(initialYear || "");
    setResults([]);
    setPicked(null);
    setStatus(null);
    setApplyPoster(true);
    setApplyDescription(true);
    // Focus the search box on next tick so it beats React's mount work.
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open, initialQuery, initialYear]);

  // Debounced search whenever q or year changes.
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setStatus(null);
      try {
        const params = new URLSearchParams({ q: q.trim() });
        if (year && /^\d{4}$/.test(String(year))) params.set("year", String(year));
        const res = await fetch(`${apiBase}/tmdb/search?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (res.status === 503) {
          setStatus({ level: "error", text: "TMDb API key not configured on the server." });
          setResults([]);
          return;
        }
        if (!res.ok) throw new Error(`Search failed (${res.status})`);
        const data = await res.json();
        setResults(data.results || []);
        if ((data.results || []).length === 0) {
          setStatus({ level: "info", text: "No matches. Try dropping the year or trimming the title." });
        }
      } catch (err) {
        setStatus({ level: "error", text: err.message || "TMDb search failed." });
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [q, year, open, apiBase, authHeaders]);

  // ESC closes; separate from App.js global handler so it wins while
  // the modal is on top of anything else.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  const applyPick = async () => {
    if (!picked) return;
    if (!applyPoster && !applyDescription) {
      toast.error("Nothing to apply", { description: "Tick at least POSTER or SYNOPSIS." });
      return;
    }
    setSaving(true);
    try {
      let res;
      if (applyOverride) {
        // Caller controls the endpoint + payload; picked entry + apply
        // flags come along as arguments so the caller can drop in what
        // it needs (e.g. channel_id for the bulk-episode flow).
        res = await applyOverride({
          picked,
          applyPoster,
          applyDescription,
        });
      } else {
        if (!targetId) return;
        const endpoint = `${apiBase}/tmdb/apply/${mode}/${targetId}`;
        res = await fetch(endpoint, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            tmdb_id: picked.tmdb_id,
            media_type: picked.media_type,
            apply_poster: applyPoster,
            apply_description: applyDescription,
          }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Apply failed (${res.status})`);
      }
      const updated = await res.json();
      toast.success("TMDb metadata applied", {
        description: `${picked.title}${picked.year ? ` (${picked.year})` : ""}`,
      });
      if (onApplied) await onApplied(updated);
      onClose();
    } catch (err) {
      toast.error("Apply failed", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const pickedOverview = picked?.overview || "";

  const modalContent = useMemo(() => {
    if (!open) return null;
    return (
      <div
        className="fixed inset-0 z-[65] bg-black/90 flex items-center justify-center p-4"
        data-testid="tmdb-lookup-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-black border-2 border-[var(--phosphor)] shadow-[0_0_30px_color-mix(in_srgb,var(--phosphor)_35%,transparent)] text-[var(--phosphor)]"
          style={{ fontFamily: "JetBrains Mono, monospace" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)]">
            <div className="flex items-center gap-2 text-[14px] tracking-[0.25em]" style={{ fontFamily: "VT323, monospace" }}>
              <Film size={14} />
              🎬 TMDB LOOKUP · {mode === "channel" ? "CHANNEL" : "TAPE"}
            </div>
            <button
              onClick={onClose}
              data-testid="tmdb-modal-close"
              className="text-[var(--phosphor)] border border-[var(--phosphor)] px-2 py-0.5 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors flex items-center gap-1"
            >
              <X size={11} /> ESC
            </button>
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-2 p-3 border-b border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]">
            <SearchIcon size={14} className="opacity-70 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Title (e.g. Bluey, Spirited Away, The Matrix)"
              data-testid="tmdb-search-input"
              className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2 py-1 text-[13px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
            />
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value.trim())}
              placeholder="Year"
              min="1888"
              max="2100"
              data-testid="tmdb-search-year"
              className="w-20 bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2 py-1 text-[13px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
            />
            {loading && (
              <span className="text-[11px] opacity-70 tracking-[0.2em] shrink-0">SEARCHING…</span>
            )}
          </div>

          {/* Status line */}
          {status && (
            <div
              data-testid="tmdb-status"
              className={`px-3 py-1.5 text-[11px] tracking-[0.2em] ${
                status.level === "error"
                  ? "text-[#FF6B6B] bg-[#FF6B6B]/10"
                  : "opacity-80 bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)]"
              }`}
            >
              {status.text}
            </div>
          )}

          {/* Results grid — 4-col responsive tiles */}
          <div className="flex-1 overflow-y-auto p-3" data-testid="tmdb-results">
            {results.length === 0 && !loading && !status && (
              <div className="text-center text-[12px] opacity-60 py-10 tracking-[0.2em]">
                Type a title above to search TMDb.
              </div>
            )}
            {results.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {results.map((r) => {
                  const isPicked = picked && picked.tmdb_id === r.tmdb_id && picked.media_type === r.media_type;
                  return (
                    <button
                      key={`${r.media_type}-${r.tmdb_id}`}
                      onClick={() => setPicked(r)}
                      data-testid={`tmdb-result-${r.tmdb_id}`}
                      className={`text-left flex flex-col border transition-all ${
                        isPicked
                          ? "border-[var(--phosphor)] shadow-[0_0_12px_color-mix(in_srgb,var(--phosphor)_60%,transparent)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"
                      }`}
                    >
                      {r.poster_thumb_url ? (
                        <img
                          src={r.poster_thumb_url}
                          alt={r.title}
                          className="w-full aspect-[2/3] object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full aspect-[2/3] flex items-center justify-center text-[10px] opacity-50">
                          NO POSTER
                        </div>
                      )}
                      <div className="p-1.5 text-[11px] leading-snug">
                        <div className="flex items-center gap-1 mb-0.5">
                          {r.media_type === "tv" ? <TvIcon size={10} /> : <Film size={10} />}
                          <span className="opacity-60 text-[9px] tracking-[0.15em]">
                            {r.media_type.toUpperCase()}{r.year ? ` · ${r.year}` : ""}
                          </span>
                        </div>
                        <div className="truncate" title={r.title}>{r.title}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Applied selection preview + apply controls */}
          {picked && (
            <div
              className="border-t-2 border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_6%,transparent)] p-3"
              data-testid="tmdb-picked-panel"
            >
              <div className="flex gap-3">
                {picked.poster_thumb_url && (
                  <img
                    src={picked.poster_thumb_url}
                    alt={picked.title}
                    className="w-16 aspect-[2/3] object-cover border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] tracking-[0.15em] mb-1" style={{ fontFamily: "VT323, monospace" }}>
                    {picked.title}{picked.year ? ` (${picked.year})` : ""}
                  </div>
                  {pickedOverview && (
                    <div className="text-[11px] opacity-85 leading-snug line-clamp-3 mb-2">
                      {pickedOverview}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 mb-2 text-[11px]">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyPoster}
                        onChange={(e) => setApplyPoster(e.target.checked)}
                        disabled={!picked.poster_url}
                        data-testid="tmdb-apply-poster"
                        className="accent-[var(--phosphor)]"
                      />
                      APPLY POSTER
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyDescription}
                        onChange={(e) => setApplyDescription(e.target.checked)}
                        disabled={!picked.overview}
                        data-testid="tmdb-apply-description"
                        className="accent-[var(--phosphor)]"
                      />
                      APPLY SYNOPSIS
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={applyPick}
                      disabled={saving || (!applyPoster && !applyDescription)}
                      data-testid="tmdb-apply-btn"
                      className="border border-[var(--phosphor)] text-[var(--phosphor)] px-3 py-1 text-[11px] tracking-[0.2em] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <Sparkles size={11} />
                      {saving ? "APPLYING…" : (applyLabel || "APPLY TO " + (mode === "channel" ? "CHANNEL" : "TAPE"))}
                    </button>
                    <button
                      onClick={() => setPicked(null)}
                      className="text-[11px] tracking-[0.2em] opacity-70 hover:opacity-100 underline underline-offset-2"
                    >
                      clear pick
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attribution footer — TMDb terms require credit. */}
          <div
            className="px-3 py-1.5 text-[9px] opacity-50 tracking-[0.15em] text-right border-t border-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
            data-testid="tmdb-attribution"
          >
            metadata &amp; images ↝ themoviedb.org · this product uses the TMDb API but is not endorsed or certified by TMDb.
          </div>
        </div>
      </div>
    );
  }, [open, q, year, results, picked, applyPoster, applyDescription, saving, loading, status, mode, targetId, apiBase, authHeaders, onClose, pickedOverview]);

  return modalContent;
}
